import React, { useState } from 'react';
import { useOps } from '../context/OpsContext';
import { useNavigate } from 'react-router-dom';
import {
    Users, UserPlus, Package, MapPin, DollarSign, FileSpreadsheet,
    PhoneCall, PlusCircle, TrendingUp, Calendar, ArrowRight, Wallet,
    Clock, CheckCircle2, AlertCircle, Car, Activity, TrendingDown, ChevronRight
} from 'lucide-react';
import ImportModal from '../components/ImportModal';
import ExportButton from '../components/ExportButton';
import './Dashboard.css';

const Dashboard = () => {
    const {
        leads, customers, packages, branches, loading,
        handleTemplateImport, payments, expenses,
        selectedBranch, setSelectedBranch,
        selectedDate, setSelectedDate
    } = useOps();
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const navigate = useNavigate();

    // Helper to check if item belongs to selected branch
    const branchMatch = (item) => {
        if (selectedBranch === 'All Branches') return true;
        const branchId = item.branch_id || item.branchId || item.branch;
        const selectedId = branches.find(b => b.branch_name === selectedBranch || b.name === selectedBranch)?.id;
        return branchId == selectedId || item.branch === selectedBranch;
    };

    // Filter Data
    const filteredPayments = (payments || []).filter(p => {
        if (!p.payment_date) return false;
        const date = new Date(p.payment_date);
        return date.getMonth() === selectedDate.month &&
            date.getFullYear() === selectedDate.year &&
            branchMatch(p);
    });

    const filteredExpenses = (expenses || []).filter(e => {
        if (!e.expense_date) return false;
        const date = new Date(e.expense_date);
        return date.getMonth() === selectedDate.month &&
            date.getFullYear() === selectedDate.year &&
            branchMatch(e);
    });

    const filteredLeads = (leads || []).filter(branchMatch);
    const filteredCustomers = (customers || []).filter(branchMatch);

    // KPI Calculations
    const mtdRevenue = filteredPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const mtdExpenses = filteredExpenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
    const balance = mtdRevenue - mtdExpenses;

    const recentLeads = filteredLeads.slice(0, 5);
    const activeStudents = filteredCustomers.filter(c => c.status === 'Active').length;
    const newLeadsCount = filteredLeads.filter(l => l.status === 'New').length;

    if (loading) return (
        <div className="h-[60vh] flex items-center justify-center">
            <div className="flex flex-col items-center gap-4 text-center">
                <div className="relative">
                    <div className="w-16 h-16 border-4 border-slate-100 rounded-full"></div>
                    <div className="absolute top-0 w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
                <div>
                    <h3 className="text-slate-800 font-bold uppercase tracking-widest text-xs">Synchronizing Intelligence</h3>
                </div>
            </div>
        </div>
    );

    const QuickActionCard = ({ title, icon: Icon, colorClass, onClick, description }) => (
        <div className="action-card" onClick={onClick}>
            <div className={`action-icon ${colorClass}`}>
                <Icon size={24} />
            </div>
            <div className="flex flex-col items-center">
                <span className="action-title">{title}</span>
                {description && <span className="text-[10px] text-slate-400 font-medium">{description}</span>}
            </div>
        </div>
    );

    const StatCard = ({ title, amount, icon: Icon, type, trend, isCurrency = true }) => (
        <div className="card stat-card fade-in">
            <div className="stat-header">
                <span className="stat-title">{title}</span>
                <Icon size={20} className={`stat-icon ${type}`} />
            </div>
            <div className="stat-amount">
                {isCurrency ? '$' : ''}{isCurrency ? parseFloat(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : amount}
            </div>
            <div className="flex items-center justify-between mt-1">
                <span className="stat-sub">This Month</span>
                {trend && <span className={`text-[10px] font-bold ${trend.startsWith('+') ? 'text-green-500' : 'text-red-500'}`}>{trend}</span>}
            </div>
        </div>
    );

    return (
        <div className="dashboard-container">
            {/* Page Title & Actions */}
            <div className="flex justify-between items-end mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Ops Center</h1>
                    <p className="text-slate-500 text-sm">Control Tower & Performance Monitoring</p>
                </div>
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-2">
                        <Calendar size={16} className="text-amber-500" />
                        <select
                            className="text-xs font-bold text-slate-700 bg-transparent border-none outline-none"
                            value={selectedDate.month}
                            onChange={e => setSelectedDate({ ...selectedDate, month: parseInt(e.target.value) })}
                        >
                            {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => (
                                <option key={i} value={i}>{m}</option>
                            ))}
                        </select>
                        <select
                            className="text-xs font-bold text-slate-700 bg-transparent border-none outline-none"
                            value={selectedDate.year}
                            onChange={e => setSelectedDate({ ...selectedDate, year: parseInt(e.target.value) })}
                        >
                            {[2023, 2024, 2025, 2026, 2027].map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                    <div className="h-8 w-[1px] bg-slate-200"></div>
                    <button
                        onClick={() => setIsImportModalOpen(true)}
                        className="btn btn-secondary flex items-center gap-2 text-xs"
                        style={{ backgroundColor: 'white', border: '1px solid #e5e7eb' }}
                    >
                        <FileSpreadsheet size={16} className="text-emerald-500" />
                        Bulk Upload
                    </button>
                    <ExportButton
                        data={filteredCustomers}
                        className="btn btn-primary flex items-center gap-2 text-xs"
                    />
                </div>
            </div>

            {/* Quick Actions */}
            <section className="quick-actions-grid">
                <QuickActionCard
                    title="Add Lead"
                    icon={UserPlus}
                    colorClass="icon-blue"
                    onClick={() => navigate('/leads')}
                />
                <QuickActionCard
                    title="Customers"
                    icon={Users}
                    colorClass="icon-green"
                    onClick={() => navigate('/customers')}
                />
                <QuickActionCard
                    title="Scheduling"
                    icon={Calendar}
                    colorClass="icon-gold"
                    onClick={() => navigate('/scheduling')}
                />
                <QuickActionCard
                    title="Vehicle Fleet"
                    icon={Car}
                    colorClass="icon-purple"
                    onClick={() => navigate('/vehicles')}
                />
            </section>

            {/* Financial Summary */}
            <section className="stats-grid">
                <StatCard title="Total Students" amount={activeStudents} icon={Users} type="blue" isCurrency={false} />
                <StatCard title="MTD Income" amount={mtdRevenue} icon={TrendingUp} type="income" trend="+12%" />
                <StatCard title="MTD Expenses" amount={mtdExpenses} icon={TrendingDown} type="expense" trend="-5%" />
                <StatCard title="Net Balance" amount={balance} icon={Wallet} type="balance" />
            </section>

            <div className="dashboard-main-grid">
                {/* Recent Leads */}
                <div className="card list-card fade-in">
                    <div className="card-header">
                        <h3 className="card-title">Recent Leads</h3>
                        <div onClick={() => navigate('/leads')} className="text-[10px] text-amber-500 font-bold flex items-center gap-1 cursor-pointer hover:underline">
                            VIEW ALL <ChevronRight size={12} />
                        </div>
                    </div>
                    <div className="list-content">
                        {recentLeads.length === 0 ? (
                            <p className="empty-state" style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af', fontSize: '13px' }}>No recent leads found.</p>
                        ) : (
                            recentLeads.map(lead => (
                                <div key={lead.id} className="list-item" onClick={() => navigate('/leads')} style={{ cursor: 'pointer' }}>
                                    <div className="item-info">
                                        <span className="item-name">{lead.name}</span>
                                        <span className="item-sub">{lead.source} • {new Date(lead.date).toLocaleDateString()}</span>
                                    </div>
                                    <span className={`status-badge ${(lead.status || '').toLowerCase()}`}>
                                        {lead.status}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* System Stats / Alerts */}
                <div className="card list-card fade-in">
                    <div className="card-header">
                        <h3 className="card-title">System Pulse</h3>
                        <Activity size={16} className="text-amber-500 animate-pulse" />
                    </div>
                    <div className="list-content" style={{ gap: '1.5rem', padding: '0.5rem 0' }}>
                        <div>
                            <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                                <span>Infrastructure Integrity</span>
                                <span className="text-emerald-500">99.9%</span>
                            </div>
                            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-amber-500 w-[99.9%]"></div>
                            </div>
                        </div>

                        <div>
                            <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                                <span>Pending Invoices</span>
                                <span className="text-rose-500">{filteredCustomers.filter(c => c.balance > 0).length} Overdue</span>
                            </div>
                            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-rose-500" style={{ width: `${(filteredCustomers.filter(c => c.balance > 0).length / (filteredCustomers.length || 1)) * 100}%` }}></div>
                            </div>
                        </div>

                        <div className="mt-2 p-4 bg-slate-50 rounded-xl border border-slate-100">
                            <div className="flex items-start gap-3">
                                <AlertCircle size={16} className="text-amber-500 mt-0.5" />
                                <div>
                                    <p className="text-xs font-bold text-slate-800">Branch Alert</p>
                                    <p className="text-[10px] text-slate-500 mt-0.5">High volume of new leads at Main HQ. Consider allocating additional staff.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ImportModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                onImport={handleTemplateImport}
                templateUrl="/wp-content/reactpress/apps/data_upload_template.xlsx"
            />
        </div>
    );
};

export default Dashboard;
